import { useMemo } from "react";
import {
  ApprovalCard,
  type MetadataItem,
  type SerializableApprovalCard,
} from "~/components/tool-ui/approval-card";
import { HitlLoadError, HitlPending } from "./HitlStatus";
import { approvalWiring } from "./approvalWiring";
import type { HitlToolProps } from "./hitlTools";
import { parseTriggerArgs, resolveDagDisplayName, type TriggerArgs } from "./publicDatasetTrigger";

function buildCard(
  id: string,
  { dagId, dagParams }: TriggerArgs,
  dagDisplayName: string | undefined
): Omit<SerializableApprovalCard, "choice"> {
  // No listing in this turn (model already knew dagId from earlier) — fall back to dag_id.
  const label = dagDisplayName ?? dagId;
  const metadata: MetadataItem[] = [{ key: "Pipeline", value: label }];
  const paramEntries = Object.entries(dagParams);

  return {
    id,
    title: `Trigger ${label}?`,
    metadata,
    sections:
      paramEntries.length > 0
        ? [
            {
              heading: "Parameter overrides",
              items: paramEntries.map(([key, value]) => ({
                label: key,
                value: Array.isArray(value) ? value.join(", ") : String(value),
              })),
            },
          ]
        : undefined,
    confirmLabel: "Trigger",
  };
}

// Reuses ApprovalCard and the same deferred-resume wiring as
// `ask_for_approval`/`confirm_ingest`.
export const PublicDatasetTriggerCard = ({
  entry,
  isRunning,
  onAnswer,
  turnToolCalls,
}: HitlToolProps) => {
  const args = useMemo(() => parseTriggerArgs(entry.args), [entry.args]);
  const dagDisplayName = useMemo(
    () => (args ? resolveDagDisplayName(turnToolCalls, args.dagId) : undefined),
    [turnToolCalls, args]
  );

  if (!args) {
    return isRunning ? (
      <HitlPending>Preparing pipeline trigger…</HitlPending>
    ) : (
      <HitlLoadError>Couldn&apos;t load this trigger request.</HitlLoadError>
    );
  }

  const card = buildCard(entry.id, args, dagDisplayName);

  return <ApprovalCard {...card} {...approvalWiring(entry, onAnswer)} />;
};
